import { Component, EventEmitter, Input, OnInit, OnDestroy, Output, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { SecondaryActionDashboardService } from './secondary-action-dashboard.service';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
@Component({
    selector: 'gtw-secondary-action-dashboard',
    templateUrl: './secondary-action-dashboard.component.html',
    styleUrls: ['./secondary-action-dashboard.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class SecondaryActionDashboardComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  @Input('base-urls')
  set _base_urls(baseUrls: string) {
    try {
      let urls: GtwElementsBaseUrlsModel = safeJsonInput<any>(baseUrls, null, 'secondary-action-dashboard.parse1');
      if (Object.keys(urls).length > 0) {
        this.baseURLs = urls;
        this.init();
      }
    } catch (e) {
      console.log('base-urls input not yet stable. Still waiting to get resolved.');
    }
  }
  baseURLs: GtwElementsBaseUrlsModel = { admin: '', api: '', custom: '', efile: '', pdfEngine: '', bulkPdfEngine: '' };

  filterParams: any = {};

  @Input('filter-params')
  set _filter_params(filterParams: string) {
    try {
      let params = safeJsonInput<any>(filterParams, null, 'secondary-action-dashboard.parse2');
      if (Object.keys(params).length > 0) {
        this.filterParams = params;
        this.init();
      }
    } catch (e) {
      console.log('Filter params input not yet stable. Still waiting to get resolved.');
    }
  }

  globalService: any;
  appService: any = {
    USER_SETTINGS: '',
    SERVER_CONFIG: ''
  };
  GENERAL_CONFIG: any;

  @Input('header-data')
  set _header_data(headerData: string) {
    try {
      let data = safeJsonInput<any>(headerData, null, 'secondary-action-dashboard.parse3');
      if (Object.keys(data).length > 0) {
        this.globalService = data.globalParams;
        this.appService.USER_SETTINGS = data.userSettingsData;
        this.appService.SERVER_CONFIG = data.serverConfigData;
        this.GENERAL_CONFIG = data.generalConfigData;
        this.init();
      }
    } catch (e) {
      console.log('Header data input not yet stable. Still waiting to get resolved.');
    }
  }

  rowData: any = {};

  @Input('row-data')
  set _row_data(rowData: string) {
    try {
      let data = safeJsonInput<any>(rowData, null, 'secondary-action-dashboard.parse4');
      if (Object.keys(data).length > 0) {
        this.rowData = data;
        this.init();
      }
    } catch (e) {
      console.log('Row data input not yet stable. Still waiting to get resolved.');
    }
  }

  // STANDARD COMPONENT PROPERTIES
  currentTabName = 'Form Entry';
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;

  // Component specific properties
  modalTitle = 'Request JSON';
  showJsonLoader = true;
  showJSON = false;
  jsonDisplayDataView = '';

  // Guard flag: prevents duplicate API calls when multiple @Input setters fire
  private dataLoaded = false;

  // Safety net: if the @Input setters never deliver a usable tracking key / base
  // url, stop the loader after inputs settle so it does not spin forever.
  private settleTimer: any = null;

  constructor(
    private _snackBar: MatSnackBar,
    private secondaryActionDashboardService: SecondaryActionDashboardService
  ) { }

  ngOnInit(): void {
    this.init();
  }

  ngOnDestroy(): void {
    this.clearSettleTimer();
  }

  init() {
    if (this.dataLoaded) {
      return;
    }
    try {
      const baseUrl = this.resolveBaseUrl();
      const trackingKey = this.resolveTrackingKey();

      if (baseUrl && trackingKey != null && String(trackingKey).trim() !== '') {
        this.dataLoaded = true;
        this.clearSettleTimer();
        this.getJsonData(trackingKey);
        return;
      }

      // Inputs arrive through several independent @Input setters and may not all
      // be present yet. Wait briefly for them to settle; if we still cannot
      // resolve a tracking key + base url, stop the loader and show the empty
      // state instead of spinning indefinitely.
      this.scheduleSettleCheck();
    } catch (e) {
      console.error('Error in SecondaryActionDashboard init:', e);
      this.showJsonLoader = false;
    }
  }

  // Base url for the action endpoint. GENERAL_CONFIG.base_url is the canonical
  // source, but (as elsewhere in the app) it falls back to the API base url, so
  // the call can still fire when only base-urls is bound and header-data is not.
  private resolveBaseUrl(): string {
    return this.GENERAL_CONFIG?.base_url
      || this.baseURLs?.api
      || this.baseURLs?.custom
      || this.baseURLs?.admin
      || '';
  }

  // Tracking key can come from the grid row (row action) or from filter params
  // (screen-level custom action); accept both casings.
  private resolveTrackingKey(): any {
    return this.rowData?.TRACKING_KEY
      ?? this.rowData?.tracking_key
      ?? this.filterParams?.TRACKING_KEY
      ?? this.filterParams?.tracking_key
      ?? null;
  }

  private scheduleSettleCheck(): void {
    this.clearSettleTimer();
    this.settleTimer = setTimeout(() => {
      this.settleTimer = null;
      if (!this.dataLoaded) {
        this.showJsonLoader = false;
        this.showJSON = false;
      }
    }, 2500);
  }

  private clearSettleTimer(): void {
    if (this.settleTimer) {
      clearTimeout(this.settleTimer);
      this.settleTimer = null;
    }
  }

  getJsonData(key: string | number) {
    this.showJsonLoader = true;
    this.showJSON = false;

    this.secondaryActionDashboardService.getJsonData(
      this.resolveBaseUrl(),
      key
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response && response.callSuccess === '1') {
          const jsonObj = response.jsonObject && response.jsonObject[0]
            ? response.jsonObject[0]
            : response.jsonObject;
          this.jsonDisplayDataView = this.objToJsonView(jsonObj);
          this.showJSON = true;
        } else {
          this._snackBar.open('No Data found', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        }
        this.showJsonLoader = false;
      },
      (error: any) => {
        console.error('Error fetching JSON data:', error);
        this.showJsonLoader = false;
        this._snackBar.open('Error fetching request data', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
      }
    );
  }

  // Convert object to formatted JSON HTML view (replaces AngularJS ObjectDiff.objToJsonView)
  objToJsonView(obj: any): string {
    try {
      return this.formatJsonWithoutQuotes(obj, 0);
    } catch (e) {
      console.error('Error converting object to JSON view:', e);
      return 'Error displaying data';
    }
  }

  private formatJsonWithoutQuotes(obj: any, indent: number): string {
    const spaces = '  '.repeat(indent);

    if (obj === null || obj === undefined) {
      return 'null';
    }

    if (typeof obj === 'string') {
      return `"${obj}"`;
    }

    if (typeof obj === 'number' || typeof obj === 'boolean') {
      return obj.toString();
    }

    if (Array.isArray(obj)) {
      if (obj.length === 0) {
        return '[]';
      }
      const items = obj.map(item => `${spaces}  ${this.formatJsonWithoutQuotes(item, indent + 1)}`);
      return `[\n${items.join(',\n')}\n${spaces}]`;
    }

    if (typeof obj === 'object') {
      const keys = Object.keys(obj);
      if (keys.length === 0) {
        return '{}';
      }
      const items = keys.map(key => {
        const value = this.formatJsonWithoutQuotes(obj[key], indent + 1);
        return `${spaces}  ${key}: ${value}`;
      });
      return `{\n${items.join(',\n')}\n${spaces}}`;
    }

    return String(obj);
  }

  cancel() {
    // CRITICAL: Use EventEmitter for AngularJS modal dismissal
    this.requestGridUpdate.emit('cancel');
  }
}
